import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { LuBan, LuFlag, LuCoins } from 'react-icons/lu';
import SafetyDialog, { DialogIcon, Spinner, btnSecondary, btnDanger, row } from './SafetyDialog';
import { safetyApi, apiError } from './safetyApi';

/**
 * Bloquear a un usuario (POST /api/v1/user/block/{id}), como en la app móvil,
 * con un paso más: después de bloquear se ofrece reportar o reclamar, porque
 * el bloqueo no mueve coins y quien fue estafado puede no saber que todavía
 * puede reclamar desde el chat (que sigue legible). onBlocked(result) se
 * llama apenas el backend confirma.
 */
export default function BlockUserModal({ open, user, onClose, afterLeave, onBlocked, onRequestReport, onGoToChat }) {
  const [step, setStep] = useState('confirm'); // confirm | done
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setStep('confirm');
      setResult(null);
      setError('');
    }
  }, [open]);

  const name = user?.nickname || user?.name;

  const block = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await safetyApi.block(user.id);
      setResult(data);
      setStep('done');
      onBlocked?.(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SafetyDialog open={open} onClose={onClose} busy={busy} afterLeave={afterLeave}>
      {step === 'confirm' && (
        <>
          <DialogIcon icon={LuBan} />
          <Dialog.Title className="mt-3 text-lg font-semibold">Bloquear usuario</Dialog.Title>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {name || 'Este usuario'} y tú ya no van a poder escribirse ni ver sus perfiles.
          </p>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            No le avisaremos. La conversación queda guardada por si necesitas reclamar un archivo o un regalo.
          </p>
          {error && <p className="mt-3 text-sm text-red-500"><b>No se pudo bloquear.</b> {error}</p>}
          <div className="mt-5 flex gap-2">
            <button onClick={onClose} disabled={busy} className={btnSecondary}>Cancelar</button>
            <button onClick={block} disabled={busy} className={btnDanger}>
              {busy && <Spinner />} Bloquear
            </button>
          </div>
        </>
      )}

      {step === 'done' && (
        <>
          <Dialog.Title className="text-lg font-semibold">Bloqueaste a {name || 'este usuario'}</Dialog.Title>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">¿Quieres además reportar o reclamar algo?</p>
          <div className="mt-4 space-y-2">
            {onRequestReport && (
              <button onClick={onRequestReport} className={row}>
                <LuFlag className="text-lg text-red-500 shrink-0" />
                <span>
                  <span className="block">Reportar a {name || 'este usuario'}</span>
                  <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">Acoso, estafa, spam u otro comportamiento que infringe las reglas.</span>
                </span>
              </button>
            )}
            {result?.can_claim && (
              <button onClick={() => onGoToChat?.(result.chat_id)} className={row}>
                <LuCoins className="text-lg text-amber-500 shrink-0" />
                <span>
                  <span className="block">Reclamar un archivo o mis coins</span>
                  <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">
                    En el chat, abre el menú del archivo o del regalo y elige “Reclamar”. Solo se pueden reclamar los últimos 20 mensajes.
                  </span>
                </span>
              </button>
            )}
          </div>
          <div className="mt-5 flex">
            <button onClick={onClose} className={btnSecondary}>Listo</button>
          </div>
        </>
      )}
    </SafetyDialog>
  );
}
